import { html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_HEALTH_DASHBOARD_INFO_OVERLAY } from '../../../webcomponents.constants';
import { getTargetForMenuStateByMenuItem } from '../../../productTrials/b2b/inProductTrialsOverlayWindow/inProductTrialsOverlayWindowsApp/view/configs/in-product-trials-overlay-window-config';
import { MenuState } from '../../../productTrials/b2b/inProductTrialsOverlayWindow/inProductTrialsOverlayWindowsApp/types/MenuState';
import { MenuItem } from '../../../productTrials/b2b/inProductTrialsOverlayWindow/inProductTrialsOverlayWindowsApp/types/MenuItem';
import { GzOverlay } from '../../../ui/gzInfoOverlay';
import { OverlayPosition } from '../../../ui/gzInfoOverlay/type';
import { NavigationConstants, NavigationHelper } from '@nirvana/shared/navigation';
import { ConsoleRequest, DataProviderEntity, DataProviderType } from '../../../utils/request';
import { translateFn } from '../../../utils/translator.helper';
import { ShouldDisplayOverlay } from '../types/types';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * @summary Type here the description of this component
 * @tag gz-health-dashboard-info-overlay
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_INFO_OVERLAY)
export class HealthDashboardInfoOverlay extends LitElement {
  /** @internal */
  private overlayComponent;

  private _menuState: MenuState;

  private firstEnrollment = false;

  set menuState(value: MenuState) {
    this._menuState = value;
    if (!this.overlayComponent) {
      return;
    }
    this.updateOverlayPosition();
  }

  static get styles() {
    return styles;
  }

  private updateOverlayPosition() {
    const targetElement = this.getMenuTargetElement();
    GzOverlay.updatePosition(this.overlayComponent, targetElement.getEl().dom);
  }

  public async buildInfoOverlay() {
    try {
      const response = await ConsoleRequest.createRequest<ShouldDisplayOverlay>({
        dataProviderType: DataProviderType.CORE,
        dataProviderEntity: DataProviderEntity.MODEL,
        endpoint: 'AdditionalUserSettings',
        method: 'shouldShowHealthDashboardPopup',
      });

      if (response.dismissedHealthDashboardInfo && response.dismissedHealthDashboardNewWidgets) {
        return;
      }

      if (!response.dismissedHealthDashboardInfo && !response.dismissedHealthDashboardNewWidgets) {
        this.firstEnrollment = true;
      }

      const targetElement = this.getMenuTargetElement();

      // build overlay component
      this.overlayComponent = GzOverlay.show(
        {
          content: this,
          position: OverlayPosition.LEFT_TOP,
        },
        targetElement.getEl().dom
      );

      if (!this.overlayComponent?.isOpen) {
        this.overlayComponent?.open();
      }

      if (targetElement.getEl().dom.parentElement) {
          this.overlayComponent.addOverlayEventListeners(
            targetElement.getEl().dom.parentElement,
            ['scroll', 'wheel', 'DOMMouseScroll', 'menuScroll'],
            this.updateOverlayPosition.bind(this)
          );
      }
    } catch (e) {
      throw new Error('Unable to display Health Dashboard popup');
    }
  }

  private getMenuTargetElement() {
    const targetsByMenuItem = getTargetForMenuStateByMenuItem(this._menuState);
    return Ext.getCmp(targetsByMenuItem[MenuItem.HealthDashboard]);
  }

  render() {
    return html`
      <div class="overlay" style="max-width: 272px">
        <div class="image-container">
          <img
            class="${this.firstEnrollment ? 'first-enrollment' : 'enrolled'}"
            style="width: 100%; height: auto;"
            src="${this.firstEnrollment
              ? 'app/webcomponents/assets/images/healthDashboard/gz-overlay-header.jpg'
              : 'app/webcomponents/assets/images/trial/patchManagement/patchManagementHeader.jpg'}"
          />
        </div>
        <div class="title-container d-flex flex-row">
          <gz-title class="title" fontSize="18"
            ><span class="title-content"
              >${unsafeHTML(
                translateFn('Health Dashboard {tag}', {
                  tag: '<span class="early">' + 'Early Access' + '</span>',
                })
              )}</span
            ></gz-title
          >
        </div>
        <div class="info-text-container d-flex flex-column">
          <gz-text class="d-inline" fontSize="14"
            ><span class="text-content"
              >${this.firstEnrollment
                ? translateFn(
                    'Ready to explore? Health Dashboard is a new feature that will revolutionize your experience with GravityZone.'
                  )
                : translateFn(
                    'A new version is available! Discover the latest updates and experience the new design of Health Dashboard.'
                  )}</span
            ></gz-text
          >
          ${this.firstEnrollment
            ? html`<gz-text fontSize="14"
                ><span class="text-content"
                  >${translateFn('Let us know your invaluable insights during this exciting early access phase.')}</span
                ></gz-text
              >`
            : nothing}
        </div>
        <div class="action-buttons d-flex flex-column">
          <gz-button type="full" color="primary" theme="dark" buttonheight="small" @click="${this.goToFeature}"
            >${translateFn('Try now')}</gz-button
          >
          <gz-button type="outline" color="white" theme="dark" buttonheight="small" @click="${this.openKbArticle}"
            >${translateFn('Learn more')}</gz-button
          >
          <gz-button type="outline" color="white" theme="dark" buttonheight="small" @click="${this.dismiss}"
            >${translateFn('Dismiss')}</gz-button
          >
        </div>
      </div>
    `;
  }

  private goToFeature() {
    Nav.to('/health-dashboard');
    this.dismiss();
  }

  private dismiss() {
    this.overlayComponent.close();
    this.setPopupDismissed();
  }

  private openKbArticle() {
    const isPartner = globalThis.Application.Main.company.isPartner();
    let link: string;
    if (this.firstEnrollment) {
      link = isPartner
        ? 'https://www.bitdefender.com/business/support/en/77211-408228-health-dashboard.html'
        : 'https://www.bitdefender.com/business/support/en/77209-408228-health-dashboard.html';
    } else {
      link = isPartner
        ? 'https://www.bitdefender.com/business/support/en/77211-78199-gravityzone-control-center.html'
        : 'https://www.bitdefender.com/business/support/en/77209-78199-gravityzone-control-center.html';
    }
    window.open(link, '_blank');
  }

  private setPopupDismissed() {
    ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      dataProviderEntity: DataProviderEntity.MODEL,
      endpoint: 'AdditionalUserSettings',
      method: 'dismissHealthDashboardPopup',
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-health-dashboard-info-overlay': HealthDashboardInfoOverlay;
  }
}
